import type { ClientGame, ClientGameCtx } from "./index";
import { sound } from "../sound";
import { Fx } from "../fx";
import { sticker, puck, cachedGrad, OUTLINE, roundRectPath } from "../draw";
import { C, TINT, SEAT_COLORS, displayFont, hudPill } from "../theme";
import { countdownOverlay, safeInsets, hudLayout, type HudItem } from "./asteroids";

const W = 480;
const H = 270;
const MINE = C.green;
const RIVAL = C.coral;
const SKIN = "#FFD9B0";
const WOOD = "#B9783F";
const MUD = "#9A6234";
const ROPE = "#E9B055";
const ROPE_MARGIN = 78;   // px from each edge where |rope| = 1

interface Dust { x: number; y: number; vx: number; vy: number; ttl: number }

export class TugClient implements ClientGame {
  private ctx!: ClientGameCtx;
  private canvas!: HTMLCanvasElement;
  private fx = new Fx();
  private raf = 0;
  private last = performance.now();
  private t = 0;

  private mySlot = 0;
  private lastPull = 0;
  private ptrX = 0;
  private ptrT = 0;
  private ptrDown = false;
  private lean = [0, 0];        // eased visual lean per side
  private windShown = 0;
  private treeSway = 0;

  private get st() { return this.ctx.room.state.tug; }

  mount(ctx: ClientGameCtx) {
    this.ctx = ctx;
    this.mySlot = ctx.mySlot;
    sound.unlock();
    this.canvas = document.createElement("canvas");
    this.canvas.id = "game-canvas";
    ctx.root.appendChild(this.canvas);

    const hint = document.createElement("div");
    hint.className = "touch-hint";
    hint.textContent = "flick / tap fast to pull";
    ctx.root.appendChild(hint);
    setTimeout(() => hint.remove(), 3200);

    this.canvas.addEventListener("pointerdown", this.onDown);
    this.canvas.addEventListener("pointermove", this.onMove);
    this.canvas.addEventListener("mousedown", this.onMouse);
    this.canvas.addEventListener("mousemove", this.onMouse);
    this.canvas.addEventListener("touchstart", this.onTouch, { passive: false });
    this.canvas.addEventListener("touchmove", this.onTouch, { passive: false });
    window.addEventListener("pointerup", this.onUp);
    window.addEventListener("mouseup", this.onUp);
    window.addEventListener("touchend", this.onUp);
    window.addEventListener("keydown", this.onKey);

    ctx.room.onMessage("t-pull", (m: any) => {
      const slot = m?.slot ?? 0;
      const s = m?.s ?? 0.5;
      this.footDust(slot, s);
      if (slot === this.mySlot) {
        sound.tugPull(s);
        try { navigator.vibrate?.(6 + s * 10); } catch { /* */ }
      }
    });
    ctx.room.onMessage("t-obstacle", (m: any) => {
      if (m?.kind === "gust") { sound.tugGust(); this.fx.shake(5, 400); this.windShown = m.dir || 1; }
    });
    ctx.room.onMessage("t-win", (m: any) => {
      const mine = m?.slot === this.mySlot;
      mine ? sound.tugWin() : sound.tugLose();
      this.fx.flash(mine ? "rgba(120,255,170,0.5)" : "rgba(255,110,120,0.45)", 320);
      this.fx.shake(9, 420);
      const kx = m?.slot === 0 ? ROPE_MARGIN : W - ROPE_MARGIN;
      const seat = SEAT_COLORS[m?.slot === 1 ? 1 : 0];
      this.fx.burst(kx, H * 0.52, { count: 40, speed: 0.16, colour: [seat, C.white, C.mango], gravity: 0.00035, life: 900, square: true });
      try { navigator.vibrate?.(mine ? [20, 40, 30] : 30); } catch { /* */ }
    });

    this.last = performance.now();
    this.loop();
  }

  unmount() {
    cancelAnimationFrame(this.raf);
    this.canvas.removeEventListener("pointerdown", this.onDown);
    this.canvas.removeEventListener("pointermove", this.onMove);
    this.canvas.removeEventListener("mousedown", this.onMouse);
    this.canvas.removeEventListener("mousemove", this.onMouse);
    this.canvas.removeEventListener("touchstart", this.onTouch);
    this.canvas.removeEventListener("touchmove", this.onTouch);
    window.removeEventListener("pointerup", this.onUp);
    window.removeEventListener("mouseup", this.onUp);
    window.removeEventListener("touchend", this.onUp);
    window.removeEventListener("keydown", this.onKey);
  }

  private dust: Dust[] = [];
  private footDust(slot: number, s: number) {
    const x = slot === 0 ? W * 0.14 : W * 0.86;
    const y = H * 0.8;
    const n = 4 + ((s * 4) | 0);
    for (let i = 0; i < n; i++) {
      this.dust.push({
        x, y,
        vx: (slot === 0 ? -1 : 1) * (0.02 + Math.random() * 0.05),
        vy: -0.02 - Math.random() * 0.04,
        ttl: 380 + Math.random() * 200,
      });
    }
  }

  private sendPull(s: number) {
    const now = performance.now();
    if (now - this.lastPull < 70) return;
    if (this.ctx.room.state.phase !== "playing") return;
    if (this.st.phase !== "pull") return;
    this.lastPull = now;
    this.ctx.room.send("pull", { s: Math.max(0.2, Math.min(1, s)) });
  }

  private onDown = (e: PointerEvent) => {
    this.ptrDown = true;
    this.ptrX = e.clientX;
    this.ptrT = performance.now();
    this.sendPull(0.62);
    e.preventDefault();
  };
  private onMove = (e: PointerEvent) => {
    if (!this.ptrDown) return;
    this.flick(e.clientX);
  };
  private onMouse = (e: MouseEvent) => {
    if (e.type === "mousedown") {
      this.ptrDown = true;
      this.ptrX = e.clientX;
      this.ptrT = performance.now();
      this.sendPull(0.62);
    } else if (this.ptrDown) {
      this.flick(e.clientX);
    }
    e.preventDefault();
  };
  private onTouch = (e: TouchEvent) => {
    const t = e.changedTouches[0];
    if (!t) return;
    if (e.type === "touchstart") { this.sendPull(0.62); this.ptrX = t.clientX; this.ptrT = performance.now(); }
    else this.flick(t.clientX);
    e.preventDefault();
  };
  private flick(cx: number) {
    const now = performance.now();
    const dt = Math.max(1, now - this.ptrT);
    const dx = cx - this.ptrX;
    const speed = Math.abs(dx) / dt;
    this.ptrX = cx;
    this.ptrT = now;
    if (speed > 0.4) this.sendPull(Math.min(1, 0.35 + speed / 2.2));
  }
  private onUp = () => { this.ptrDown = false; };
  private onKey = (e: KeyboardEvent) => {
    if (e.code === "Space" || e.code === "Enter" || e.code === "ArrowLeft" || e.code === "ArrowRight") {
      this.sendPull(0.7);
      e.preventDefault();
    }
  };

  private loop = () => {
    const now = performance.now();
    const dt = Math.min(50, now - this.last);
    this.last = now;
    this.t += dt;
    try { this.draw(dt); } catch (err) { console.error("tug frame", err); }
    this.raf = requestAnimationFrame(this.loop);
  };

  private draw(dt: number) {
    const c = this.canvas;
    const g = c.getContext("2d")!;
    const dpr = Math.min(2, devicePixelRatio || 1);
    const cw = c.clientWidth, ch = c.clientHeight;
    if (cw < 40 || ch < 40) return;
    if (c.width !== cw * dpr || c.height !== ch * dpr) { c.width = cw * dpr; c.height = ch * dpr; }
    g.save();
    g.scale(dpr, dpr);

    const st = this.st;

    // ---- HUD layout (screen space, drawn last) ----
    const ins = safeInsets();
    let items: HudItem[];
    if (st.solo) {
      items = [
        { text: String(st.wins[this.mySlot] ?? 0), label: "cleared" },
        { text: String(st.level), label: "level" },
      ];
    } else {
      const pipItem = (filled: number, col: string): HudItem => ({
        w: 3 * 15 - 3,
        paint: (pg, x, y, h) => {
          for (let i = 0; i < 3; i++) {
            pg.beginPath();
            pg.arc(x + h * 0.3 + 6 + i * 15, y + h / 2, 6, 0, Math.PI * 2);
            pg.fillStyle = i < filled ? col : C.line;
            pg.fill();
            pg.lineWidth = 2;
            pg.strokeStyle = C.ink;
            pg.stroke();
          }
        },
      });
      items = [
        pipItem(st.wins[0] ?? 0, SEAT_COLORS[0]),
        { text: String(st.level), label: "level" },
        pipItem(st.wins[1] ?? 0, SEAT_COLORS[1]),
      ];
    }
    const hud = hudLayout(g, cw, ins.top + 8, items, this.ctx.room.state.mode === "solo");

    // The 480×270 world is scaled to the full width (or full height on a wide
    // screen) and the sky/grass simply carry on past its edges. Input is tap /
    // flick speed only, so nothing maps screen points into the world.
    const top = hud.bottom + 8;
    const availH = Math.max(40, ch - top - ins.bottom - 12);
    const scale = Math.min(cw / W, availH / H);
    const ox = (cw - W * scale) / 2;
    const oy = top + (availH - H * scale) / 2;
    // the visible area in world units (padded so screenshake never shows an edge)
    const xL = -ox / scale - 20, xR = (cw - ox) / scale + 20;
    const yT = -oy / scale - 20, yB = (ch - oy) / scale + 20;

    this.fx.update(dt);
    const sh = this.fx.shakeXY();

    g.save();
    g.translate(ox + sh.x, oy + sh.y);
    g.scale(scale, scale);

    // ---- sky ----
    const skyTop = Math.round(yT);
    const sky = cachedGrad(`tug-sky-${skyTop}`, () => {
      const gr = g.createLinearGradient(0, skyTop, 0, H * 0.7);
      gr.addColorStop(0, "#AFC6FF");
      gr.addColorStop(0.55, TINT.cobalt);
      gr.addColorStop(1, "#EEF2FF");
      return gr;
    });
    g.fillStyle = sky;
    g.fillRect(xL, yT, xR - xL, H * 0.8 - yT);

    // sun + drifting clouds
    puck(g, W * 0.84, H * 0.15, 15, C.mango);
    for (let i = 0; i < 3; i++) {
      const span = xR - xL + 120;
      const cxp = xL - 60 + ((i * span) / 3 + this.t * 0.006 * (1 + i * 0.3)) % span;
      this.cloud(g, cxp, H * (0.12 + i * 0.09) - i * 6, 1 - i * 0.18);
    }

    // hills
    g.beginPath();
    const hx0 = Math.floor(xL / 40) * 40;
    g.moveTo(hx0, H * 0.8);
    for (let x = hx0; x <= xR + 40; x += 20) g.lineTo(x, H * 0.66 + Math.sin(x * 0.03 + 1) * 12);
    g.lineTo(xR + 40, H * 0.8);
    g.closePath();
    g.fillStyle = TINT.mint;
    g.fill();
    g.lineWidth = 2;
    g.lineJoin = "round";
    g.strokeStyle = OUTLINE;
    g.stroke();

    // ground (runs to the bottom of the screen)
    g.fillStyle = C.green;
    g.fillRect(xL, H * 0.78, xR - xL, yB - H * 0.78);
    g.fillStyle = "rgba(255,255,255,0.18)";
    g.fillRect(xL, H * 0.78 + 3, xR - xL, 6);
    g.strokeStyle = OUTLINE;
    g.lineWidth = 2.5;
    g.beginPath(); g.moveTo(xL, H * 0.78); g.lineTo(xR, H * 0.78); g.stroke();

    // mud pit in the middle
    sticker(g, () => { roundRectPath(g, W / 2 - 46, H * 0.79, 92, 20, 8); }, {
      fill: MUD, shadow: false, outlineW: 2, highlight: "rgba(255,255,255,0.25)",
    });

    // tree that bends with wind
    this.treeSway += ((st.wind * 90000) - this.treeSway) * 0.05;
    const bend = this.treeSway + Math.sin(this.t * 0.002) * 0.5;
    g.save();
    g.translate(38, H * 0.78);
    g.rotate(bend * 0.06);
    sticker(g, () => { g.beginPath(); g.moveTo(-3, 0); g.lineTo(-5, -34); g.lineTo(5, -34); g.lineTo(3, 0); g.closePath(); }, { fill: WOOD, shadow: false, outlineW: 2 });
    sticker(g, () => { g.beginPath(); g.arc(0, -40, 15, 0, Math.PI * 2); }, { fill: C.mint, highlight: TINT.mint, outlineW: 2 });
    g.restore();

    // ---- rope + knot ----
    const knotX = W / 2 + st.rope * (W / 2 - ROPE_MARGIN);
    const ropeY = H * 0.56;
    const anchorL = W * 0.19, anchorR = W * 0.81;

    // win zone lines
    g.strokeStyle = "rgba(27,21,52,0.35)";
    g.setLineDash([4, 5]);
    g.lineWidth = 1.5;
    [ROPE_MARGIN, W - ROPE_MARGIN].forEach((x) => { g.beginPath(); g.moveTo(x, ropeY - 40); g.lineTo(x, ropeY + 46); g.stroke(); });
    g.setLineDash([]);

    // the rope (two slack curves toward each hauler): ink rim, rope, braid
    g.lineCap = "round";
    const sag = 8 + Math.sin(this.t * 0.01) * 1.5;
    g.beginPath();
    g.moveTo(anchorL, ropeY + 4);
    g.quadraticCurveTo((anchorL + knotX) / 2, ropeY + sag, knotX, ropeY);
    g.quadraticCurveTo((anchorR + knotX) / 2, ropeY + sag, anchorR, ropeY + 4);
    g.strokeStyle = OUTLINE;
    g.lineWidth = 8.5;
    g.stroke();
    g.strokeStyle = ROPE;
    g.lineWidth = 5;
    g.stroke();
    g.strokeStyle = WOOD;
    g.lineWidth = 1.6;
    g.setLineDash([3, 4]);
    g.stroke();
    g.setLineDash([]);

    // centre knot / flag
    sticker(g, () => { g.beginPath(); g.arc(knotX, ropeY, 7, 0, Math.PI * 2); }, { fill: C.mango, highlight: TINT.mango, outlineW: 2 });
    g.save();
    g.translate(knotX, ropeY - 7);
    const flap = Math.sin(this.t * 0.02) * 3 + (st.wind > 0 ? 4 : -4);
    sticker(g, () => { g.beginPath(); g.moveTo(0, 0); g.lineTo(0, -22); g.lineTo(18 + flap, -18); g.lineTo(14, -8); g.closePath(); }, { fill: C.grape, shadow: false, outlineW: 2 });
    g.restore();

    // ---- haulers (seat colours: slot 0 coral on the left, slot 1 cobalt on the right) ----
    this.lean[0] += ((-clamp(st.rope, -1, 1) * 0.5 - 0.35 - st.pull0 * 0.25) - this.lean[0]) * 0.18;
    this.lean[1] += ((clamp(st.rope, -1, 1) * 0.5 - 0.35 - st.pull1 * 0.25) - this.lean[1]) * 0.18;
    this.hauler(g, anchorL - 6, H * 0.78, this.lean[0], SEAT_COLORS[0], this.mySlot === 0, st.obstacle === "mud0");
    this.hauler(g, anchorR + 6, H * 0.78, -this.lean[1], SEAT_COLORS[1], this.mySlot === 1, st.obstacle === "mud1", true);

    // foot dust
    for (let i = this.dust.length - 1; i >= 0; i--) {
      const d = this.dust[i]!;
      d.ttl -= dt; if (d.ttl <= 0) { this.dust.splice(i, 1); continue; }
      d.x += d.vx * dt; d.y += d.vy * dt; d.vy += 0.00012 * dt;
      g.globalAlpha = Math.min(1, d.ttl / 300);
      g.fillStyle = "#D8B07A";
      g.beginPath(); g.arc(d.x, d.y, 2.4, 0, Math.PI * 2); g.fill();
    }
    g.globalAlpha = 1;

    // wind streaks
    this.windShown += (Math.sign(st.wind) * Math.min(1, Math.abs(st.wind) * 120000) - this.windShown) * 0.05;
    if (Math.abs(this.windShown) > 0.05 || st.obstacle === "gust") {
      const dir = st.obstacle === "gust" ? (this.windShown || 1) : this.windShown;
      g.strokeStyle = "rgba(255,255,255,0.9)";
      g.lineWidth = 2.5;
      for (let i = 0; i < 6; i++) {
        const yy = 40 + ((this.t * 0.35 * dir + i * 60) % (H - 60));
        const len = 26 * Math.abs(dir) + 10;
        g.beginPath(); g.moveTo(W / 2 - len * Math.sign(dir), yy); g.lineTo(W / 2 + len * Math.sign(dir), yy + 6); g.stroke();
      }
    }

    this.fx.draw(g);

    // ---- won banner ----
    if (st.phase === "won") {
      const mine = st.wonBy === this.mySlot;
      const text = mine ? "Round won!" : "Round lost";
      g.font = displayFont(32);
      g.textAlign = "center"; g.textBaseline = "middle";
      g.lineJoin = "round";
      g.lineWidth = 7; g.strokeStyle = OUTLINE;
      g.strokeText(text, W / 2, H * 0.3);
      g.fillStyle = mine ? MINE : RIVAL;
      g.fillText(text, W / 2, H * 0.3);
    }

    g.restore();

    // ---- HUD (screen space) ----
    hud.draw();
    if (st.obstacle.startsWith("mud") || st.obstacle === "gust") {
      const mud = st.obstacle !== "gust";
      const below = oy + H * scale + 12;
      const chipY = below + 34 < ch - ins.bottom - 8 ? below : oy + (H - 34) * scale;
      hudPill(g, cw / 2, chipY, mud ? "Mud — pulls weakened" : "Gust!", {
        align: "center", size: 14, fill: mud ? TINT.mango : TINT.cobalt,
      });
    }

    this.fx.drawFlash(g, cw, ch);
    if (this.ctx.room.state.phase === "countdown") countdownOverlay(g, cw, ch, this.ctx.room.state.countdown);

    g.restore();
  }

  /** a white sticker cloud: ink-grown circles first, then the white ones, so the outline has no inner seams */
  private cloud(g: CanvasRenderingContext2D, x: number, y: number, k: number) {
    const blobs: [number, number, number][] = [[-14, 2, 9], [0, -4, 12], [15, 2, 9], [4, 5, 9]];
    g.fillStyle = OUTLINE;
    for (const [bx, by, r] of blobs) { g.beginPath(); g.arc(x + bx * k, y + by * k, r * k + 2, 0, Math.PI * 2); g.fill(); }
    g.fillStyle = C.white;
    for (const [bx, by, r] of blobs) { g.beginPath(); g.arc(x + bx * k, y + by * k, r * k, 0, Math.PI * 2); g.fill(); }
  }

  private hauler(
    g: CanvasRenderingContext2D,
    x: number, groundY: number, lean: number, col: string,
    isMe: boolean, muddy: boolean, faceLeft = false,
  ) {
    g.save();
    g.translate(x, groundY);
    const f = faceLeft ? -1 : 1;
    g.scale(f, 1);
    g.rotate(lean * 0.5);
    // legs braced
    sticker(g, () => { g.beginPath(); g.moveTo(-2, 0); g.lineTo(-12, 2); g.lineTo(-10, 8); g.lineTo(0, 4); g.closePath(); }, { fill: C.ink, shadow: false, outlineW: 2 });
    sticker(g, () => { g.beginPath(); g.moveTo(2, 0); g.lineTo(10, -2); g.lineTo(12, 4); g.lineTo(4, 4); g.closePath(); }, { fill: C.ink, shadow: false, outlineW: 2 });
    // body
    sticker(g, () => { roundRectPath(g, -8, -34, 16, 34, 7); }, { fill: muddy ? MUD : col, highlight: "rgba(255,255,255,0.45)", outlineW: 2.2 });
    // head
    sticker(g, () => { g.beginPath(); g.arc(0, -42, 8, 0, Math.PI * 2); }, { fill: SKIN, highlight: "rgba(255,255,255,0.5)", outlineW: 2.2 });
    // arm reaching forward to the rope
    g.strokeStyle = OUTLINE; g.lineWidth = 5.5; g.lineCap = "round";
    g.beginPath(); g.moveTo(4, -26); g.lineTo(16, -14); g.stroke();
    g.strokeStyle = SKIN; g.lineWidth = 3.4;
    g.beginPath(); g.moveTo(4, -26); g.lineTo(16, -14); g.stroke();
    if (isMe) {
      g.scale(f, 1);
      g.font = displayFont(12); g.textAlign = "center"; g.textBaseline = "alphabetic";
      g.lineJoin = "round"; g.lineWidth = 3.5; g.strokeStyle = OUTLINE;
      g.strokeText("You", 0, -56);
      g.fillStyle = C.white;
      g.fillText("You", 0, -56);
    }
    g.restore();
  }

  results(ctx: ClientGameCtx) {
    const st = ctx.room.state.tug;
    const d = document.createElement("div");
    d.className = "arc-final";
    if (st.solo) {
      d.innerHTML = `<div class="score-big">${st.wins[ctx.mySlot] ?? 0}</div><div class="arc-note">levels cleared</div>`;
    } else {
      d.innerHTML = `<div class="score-big">${st.wins[0] ?? 0} : ${st.wins[1] ?? 0}</div><div class="arc-note">rounds</div>`;
    }
    return d;
  }
}

function clamp(v: number, a: number, b: number) { return v < a ? a : v > b ? b : v; }
